import { useMemo, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CalendarRange, CheckCircle2, Clock, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MONTHS, formatMoney, monthTotals } from "@/lib/services";

const Kpi = ({ icon: Icon, label, value, hint, cls, testid }) => (
  <div className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-4" data-testid={testid}>
    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400"><Icon className={`h-4 w-4 ${cls}`} /> {label}</p>
    <p className={`mt-2 font-mono text-2xl font-bold ${cls}`} data-testid={`${testid}-value`}>{formatMoney(value)}</p>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

const short = (v) => (v >= 1000 ? `R$ ${(v / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mil` : `R$ ${Math.round(v)}`);

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-950/95 px-3 py-2 text-xs shadow-xl">
      <p className="mb-1 font-semibold text-slate-100">{label}</p>
      <p className="text-emerald-300">Realizado: {formatMoney(d.done)}</p>
      <p className="text-sky-300">Previsto: {formatMoney(d.planned)}</p>
      <p className="mt-1 text-slate-200">Total: {formatMoney(d.done + d.planned)}</p>
    </div>
  );
};

const RevenueChart = ({ data, month, onPick }) => (
  <div className="h-72 w-full" data-testid="revenue-chart">
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="#334155" strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="name" interval={0} tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} tickLine={false} />
        <YAxis tickFormatter={short} tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} width={72} />
        <Tooltip content={<ChartTip />} cursor={{ fill: "rgba(148,163,184,0.08)" }} />
        <Bar dataKey="done" stackId="r" fill="#34D399" name="Realizado" onClick={(_, i) => onPick(i)} className="cursor-pointer" />
        <Bar dataKey="planned" stackId="r" fill="#38BDF8" fillOpacity={0.55} radius={[4, 4, 0, 0]} name="Previsto" onClick={(_, i) => onPick(i)} className="cursor-pointer" />
        <Line type="linear" dataKey="total" stroke="#F8FAFC" strokeWidth={2} dot={(p) => <circle key={p.index} cx={p.cx} cy={p.cy} r={p.index === month ? 5 : 3} fill={p.index === month ? "#FBBF24" : "#F8FAFC"} />} name="Total" />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

const MonthDetail = ({ name, totals, onOpenMonth }) => (
  <section className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-4" data-testid="dashboard-month-detail">
    <div className="mb-3 flex items-center justify-between gap-2">
      <h3 className="font-display text-lg font-bold">{name}</h3>
      <Button size="sm" variant="outline" onClick={onOpenMonth} className="rounded-full border-slate-700 bg-transparent" data-testid="dashboard-open-month"><CalendarRange className="mr-1 h-4 w-4" /> Ver serviços</Button>
    </div>
    {totals.rows.length === 0 ? <p className="text-sm text-slate-500">Nenhum serviço neste mês.</p> : (
      <>
        <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 pb-1 text-[11px] uppercase tracking-wider text-slate-500"><span>Serviço</span><span className="text-right">Realizado</span><span className="text-right">Previsto</span></div>
        {totals.rows.map((r) => (
          <div key={r.key} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 py-1 text-sm" data-testid={`dashboard-month-row-${r.key}`}>
            <span className="flex min-w-0 items-center gap-2"><span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: r.hex }} /><span className="truncate">{r.label}</span></span>
            <span className="text-right font-mono text-emerald-300">{formatMoney(r.done)}</span>
            <span className="text-right font-mono text-sky-300">{formatMoney(r.planned)}</span>
          </div>
        ))}
        <div className="mt-2 grid grid-cols-[1fr_auto_auto] gap-x-4 border-t border-slate-700/60 pt-2 text-sm font-bold">
          <span>Total</span><span className="text-right font-mono text-emerald-300" data-testid="dashboard-month-done">{formatMoney(totals.done)}</span><span className="text-right font-mono text-sky-300" data-testid="dashboard-month-planned">{formatMoney(totals.planned)}</span>
        </div>
      </>
    )}
  </section>
);

export const RevenueDashboard = ({ services, year, today, onOpenMonth }) => {
  const [month, setMonth] = useState(() => (Number(today.slice(0, 4)) === year ? Number(today.slice(5, 7)) - 1 : 0));
  const months = useMemo(() => MONTHS.map((name, i) => {
    const t = monthTotals(services.filter((s) => s.execution_date.startsWith(`${year}-${String(i + 1).padStart(2, "0")}`)));
    return { name: name.slice(0, 3), full: name, ...t, total: t.done + t.planned };
  }), [services, year]);
  const done = months.reduce((a, m) => a + m.done, 0);
  const planned = months.reduce((a, m) => a + m.planned, 0);
  const missing = months.reduce((a, m) => a + m.missing, 0);
  const cur = months[month];

  return (
    <section className="space-y-4" data-testid="revenue-dashboard">
      <div>
        <h2 className="font-display text-2xl font-bold">Faturamento {year}</h2>
        <p className="text-sm text-slate-400">Realizado = serviços já feitos. Previsto = serviços agendados ainda não feitos. Troque o ano no topo da página.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi icon={CheckCircle2} label="Realizado no ano" value={done} cls="text-emerald-300" testid="kpi-year-done" />
        <Kpi icon={Clock} label="Previsto no ano" value={planned} cls="text-sky-300" testid="kpi-year-planned" />
        <Kpi icon={TrendingUp} label="Total do ano" value={done + planned} hint="Realizado + previsto" cls="text-slate-100" testid="kpi-year-total" />
        <Kpi icon={CalendarRange} label={`${cur.full}`} value={cur.total} hint={`Realizado ${formatMoney(cur.done)} · previsto ${formatMoney(cur.planned)}`} cls="text-amber-300" testid="kpi-month-total" />
      </div>
      {missing > 0 && <p className="text-xs text-amber-300" data-testid="dashboard-missing">{missing} serviço(s) do ano sem valor não entram na soma. Preencha o valor na ficha do cliente ou no lançamento.</p>}
      <div className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-4">
        <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
          <span className="font-semibold uppercase tracking-wider">Faturamento por mês</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-400" /> Realizado</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-sky-400/60" /> Previsto</span>
          <span className="flex items-center gap-1"><span className="h-0.5 w-4 bg-slate-100" /> Total</span>
          <span className="ml-auto">Toque numa barra para ver o mês</span>
        </div>
        <RevenueChart data={months} month={month} onPick={setMonth} />
        <div className="thin-scroll mt-3 flex gap-1.5 overflow-x-auto pb-1" data-testid="dashboard-month-chips">
          {months.map((m, i) => (
            <button key={m.name} onClick={() => setMonth(i)} data-testid={`dashboard-month-${i + 1}`}
              className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${i === month ? "border-amber-300 bg-amber-300 text-amber-950" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>{m.name}</button>
          ))}
        </div>
      </div>
      <MonthDetail name={`${cur.full} ${year}`} totals={cur} onOpenMonth={() => onOpenMonth(month)} />
    </section>
  );
};
